import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../config";
import {
  FileDown,
  PhoneCall,
  BarChart3,
  CheckCircle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";

const MarketingExpenseTable = ({ dateRange }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [totals, setTotals] = useState({
    marketing_spent: 0,
    total_calls: 0,
    MCO: 0,
    bookings: 0,
  });

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // ========================= FETCH DATA =========================
  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return;

    const addHoursUTC = (isoString, hours) => {
      const date = new Date(
        isoString.endsWith("Z") ? isoString : isoString + "Z"
      );
      date.setUTCHours(date.getUTCHours() + hours);
      return date.toISOString();
    };

    const start = addHoursUTC(dateRange.startDate, 5);
    const end = addHoursUTC(dateRange.endDate, 5);

    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}get_marketing_summary.php?startDate=${encodeURIComponent(
            start
          )}&endDate=${encodeURIComponent(end)}`,
          { credentials: "include" }
        );

        const json = await res.json();

        if (json.status === "success") {
          setSummaryData(json.summary || []);

          const total = json.summary?.find((r) => r.queue_name === "Total");
          if (total) setTotals(total);
        } else {
          setSummaryData([]);
        }
      } catch (err) {
        console.error("Summary fetch error:", err);
        setSummaryData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [dateRange]);

  // ========================= AVERAGES =========================
  const averages = (() => {
    if (!summaryData.length) return {};

    const valid = summaryData.filter((r) => r.queue_name !== "Total");

    const avg = (fn) =>
      valid.reduce((a, b) => a + fn(b), 0) / valid.length || 0;

    return {
      marketing_spent: avg((r) => r.marketing_spent),
      callCost: avg((r) =>
        r.total_calls > 0 ? r.marketing_spent / r.total_calls : 0
      ),
      MCO: avg((r) => r.MCO),
      bookings: avg((r) => r.bookings),
      conversion: avg((r) =>
        r.total_calls > 0 ? (r.bookings / r.total_calls) * 100 : 0
      ),
      revenueRatio: avg((r) =>
        r.marketing_spent > 0 ? r.MCO / r.marketing_spent : 0
      ),
    };
  })();

  // ========================= COLOR & TREND HELPERS =========================
  const getColorClass = (value, avg, reverse = false) => {
    if (!avg || avg === 0) return "text-slate-300";
    const ratio = value / avg;
    
    if (reverse) {
      if (ratio < 0.9) return "text-emerald-400 font-semibold";
      if (ratio > 1.1) return "text-red-400 font-semibold";
    } else {
      if (ratio > 1.1) return "text-emerald-400 font-semibold";
      if (ratio < 0.9) return "text-red-400 font-semibold";
    }
    return "text-slate-300";
  };

  const getTrendIcon = (value, avg, reverse = false) => {
    if (!avg || avg === 0) return null;
    const ratio = value / avg;
    
    if (reverse) {
      if (ratio < 0.9) return <TrendingUp className="inline w-3 h-3 ml-1" />;
      if (ratio > 1.1) return <TrendingDown className="inline w-3 h-3 ml-1" />;
    } else {
      if (ratio > 1.1) return <TrendingUp className="inline w-3 h-3 ml-1" />;
      if (ratio < 0.9) return <TrendingDown className="inline w-3 h-3 ml-1" />;
    }
    return <Minus className="inline w-3 h-3 ml-1 opacity-30" />;
  };

  // ========================= SORTING =========================
  const handleSort = (field) => {
    if (sortField === field)
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedData = (() => {
    const total = summaryData.find((r) => r.queue_name === "Total");
    const rows = summaryData.filter((r) => r.queue_name !== "Total");

    const getValue = (row) => {
      switch (sortField) {
        case "callCost":
          return row.total_calls
            ? row.marketing_spent / row.total_calls
            : 0;
        case "conversion":
          return row.total_calls
            ? (row.bookings / row.total_calls) * 100
            : 0;
        case "revenueRatio":
          return row.marketing_spent
            ? row.MCO / row.marketing_spent
            : 0;
        default:
          return Number(row[sortField]) || 0;
      }
    };

    if (!sortField) return total ? [...rows, total] : rows;

    const sorted = rows.sort((a, b) =>
      sortOrder === "asc"
        ? getValue(a) - getValue(b)
        : getValue(b) - getValue(a)
    );

    return total ? [...sorted, total] : sorted;
  })();

  // ========================= EXCEL EXPORT =========================
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Summary");

    sheet.addRow([
      "MC",
      "Marketing Spent",
      "Calls",
      "Call Cost",
      "MCO",
      "Bookings",
      "Conversion (%)",
      "Revenue Ratio",
    ]);

    summaryData.forEach((r) => {
      sheet.addRow([
        r.queue_name,
        r.marketing_spent,
        r.total_calls,
        r.total_calls ? r.marketing_spent / r.total_calls : 0,
        r.MCO,
        r.bookings,
        r.total_calls ? (r.bookings / r.total_calls) * 100 : 0,
        r.marketing_spent ? r.MCO / r.marketing_spent : 0,
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "MarketingSummary.xlsx");
  };

  // ========================= SUMMARY CARDS CONFIG =========================
  const summaryCards = [
    {
      label: "Total Marketing Spend",
      value: `$${(totals.marketing_spent || 0).toFixed(2)}`,
      icon: DollarSign,
      gradient: "from-emerald-500/20 to-emerald-600/5",
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-400",
      borderColor: "border-emerald-500/30",
    },
    {
      label: "Total Calls",
      value: totals.total_calls || 0,
      icon: PhoneCall,
      gradient: "from-blue-500/20 to-blue-600/5",
      iconBg: "bg-blue-500/10",
      iconColor: "text-blue-400",
      borderColor: "border-blue-500/30",
    },
    {
      label: "Total Revenue",
      value: `$${(totals.MCO || 0).toFixed(2)}`,
      icon: BarChart3,
      gradient: "from-cyan-500/20 to-cyan-600/5",
      iconBg: "bg-cyan-500/10",
      iconColor: "text-cyan-400",
      borderColor: "border-cyan-500/30",
    },
    {
      label: "Total Bookings",
      value: totals.bookings || 0,
      icon: CheckCircle,
      gradient: "from-purple-500/20 to-purple-600/5",
      iconBg: "bg-purple-500/10",
      iconColor: "text-purple-400",
      borderColor: "border-purple-500/30",
    },
  ];

  // ========================= LOADING STATE =========================
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-800/50 rounded-xl" />
            ))}
          </div>
          <div className="h-96 bg-slate-800/50 rounded-xl" />
        </div>
      </div>
    );
  }

  // ========================= MAIN UI =========================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
       
        
      
      </div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
      >
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`
                relative overflow-hidden
                rounded-xl
                bg-slate-800/50
                backdrop-blur-sm
                border ${card.borderColor}
                p-5
                transition-all duration-300
                hover:scale-[1.02]
                hover:shadow-2xl
                hover:bg-slate-800/70
              `}
            >
              {/* Background gradient */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-50`}
              />

              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                      {card.label}
                    </p>
                    <h3 className="text-2xl font-bold text-white">
                      {card.value}
                    </h3>
                  </div>
                  <div className={`${card.iconBg} p-3 rounded-lg`}>
                    <Icon className={`w-5 h-5 ${card.iconColor}`} />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden"
      >
        <div className="p-6 border-b border-slate-700/50">
          <h2 className="text-xl font-semibold text-white">
            Performance Overview
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-900/50">
                <th className="px-6 py-4 text-left">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    MC
                  </span>
                </th>

                <th className="px-6 py-4 text-center">
                  <button
                    onClick={() => handleSort("marketing_spent")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Spent
                    {sortField === "marketing_spent" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>

                <th className="px-6 py-4 text-center">
                  <button
                    onClick={() => handleSort("total_calls")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Calls
                    {sortField === "total_calls" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>

                <th className="px-6 py-4 text-center">
                  <button
                    onClick={() => handleSort("callCost")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Avg Call Cost
                    {sortField === "callCost" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>

                {/* Blue-bordered section */}
                <th className="px-6 py-4 text-center bg-blue-500/5 border-l-2 border-blue-500/50">
                  <button
                    onClick={() => handleSort("MCO")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Revenue
                    {sortField === "MCO" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>

                <th className="px-6 py-4 text-center bg-blue-500/5">
                  <button
                    onClick={() => handleSort("bookings")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Bookings
                    {sortField === "bookings" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>

                <th className="px-6 py-4 text-center bg-blue-500/5">
                  <button
                    onClick={() => handleSort("conversion")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Conversion %
                    {sortField === "conversion" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>

                <th className="px-6 py-4 text-center bg-blue-500/5 border-r-2 border-blue-500/50">
                  <button
                    onClick={() => handleSort("revenueRatio")}
                    className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                  >
                    Revenue Ratio
                    {sortField === "revenueRatio" && (
                      <span className="text-blue-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-700/50">
              {sortedData.map((row, index) => {
                const callCost = row.total_calls
                  ? row.marketing_spent / row.total_calls
                  : 0;
                const conversion = row.total_calls
                  ? (row.bookings / row.total_calls) * 100
                  : 0;
                const revenueRatio = row.marketing_spent
                  ? row.MCO / row.marketing_spent
                  : 0;
                const isTotal = row.queue_name === "Total";

                return (
                  <tr
                    key={index}
                    className={`
                      transition-colors duration-150
                      ${
                        isTotal
                          ? "bg-blue-500/10 font-semibold"
                          : "hover:bg-slate-700/30"
                      }
                    `}
                  >
                    <td className="px-6 py-4">
                      <span
                        className={`font-medium ${
                          isTotal ? "text-white" : "text-slate-200"
                        }`}
                      >
                        {row.queue_name}
                      </span>
                    </td>

                    <td
                      className={`px-6 py-4 text-center ${getColorClass(
                        row.marketing_spent,
                        averages.marketing_spent
                      )}`}
                    >
                      ${row.marketing_spent.toFixed(2)}
                      {!isTotal &&
                        getTrendIcon(
                          row.marketing_spent,
                          averages.marketing_spent
                        )}
                    </td>

                    <td className="px-6 py-4 text-center text-slate-300">
                      {row.total_calls}
                    </td>

                    <td
                      className={`px-6 py-4 text-center ${getColorClass(
                        callCost,
                        averages.callCost,
                        true
                      )}`}
                    >
                      ${callCost.toFixed(2)}
                      {!isTotal &&
                        getTrendIcon(callCost, averages.callCost, true)}
                    </td>

                    {/* Blue-bordered section */}
                    <td
                      className={`px-6 py-4 text-center bg-blue-500/5 border-l-2 border-blue-500/50 ${getColorClass(
                        row.MCO,
                        averages.MCO
                      )}`}
                    >
                      ${row.MCO.toFixed(2)}
                      {!isTotal && getTrendIcon(row.MCO, averages.MCO)}
                    </td>

                    <td
                      className={`px-6 py-4 text-center bg-blue-500/5 ${getColorClass(
                        row.bookings,
                        averages.bookings
                      )}`}
                    >
                      {row.bookings}
                      {!isTotal &&
                        getTrendIcon(row.bookings, averages.bookings)}
                    </td>

                    <td
                      className={`px-6 py-4 text-center bg-blue-500/5 ${getColorClass(
                        conversion,
                        averages.conversion
                      )}`}
                    >
                      {conversion.toFixed(2)}%
                      {!isTotal &&
                        getTrendIcon(conversion, averages.conversion)}
                    </td>

                    <td
                      className={`px-6 py-4 text-center bg-blue-500/5 border-r-2 border-blue-500/50 ${getColorClass(
                        revenueRatio,
                        averages.revenueRatio
                      )}`}
                    >
                      {revenueRatio.toFixed(2)}
                      {!isTotal &&
                        getTrendIcon(revenueRatio, averages.revenueRatio)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Bottom Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        {[
          {
            label: "Total Conversion",
            value: `${(
              ((totals.bookings / totals.total_calls) * 100) ||
              0
            ).toFixed(2)}%`,
            icon: TrendingUp,
          },
          {
            label: "Avg Call/Person",
            value: `$${(
              totals.marketing_spent / totals.total_calls || 0
            ).toFixed(2)}`,
            icon: PhoneCall,
          },
          {
            label: "Avg MCO per Call",
            value: `$${(totals.MCO / totals.total_calls || 0).toFixed(2)}`,
            icon: DollarSign,
          },
          {
            label: "Revenue per Booking",
            value: `$${(totals.MCO / totals.bookings || 0).toFixed(2)}`,
            icon: BarChart3,
          },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div
              key={i}
              className="bg-slate-800/30 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4 hover:bg-slate-800/50 transition-colors duration-200"
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-500/10 p-2 rounded-lg">
                  <Icon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">
                    {stat.label}
                  </p>
                  <p className="text-xl font-bold text-white">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MarketingExpenseTable;